import { motion } from "framer-motion";
import { ArrowUpRight } from "@phosphor-icons/react";

const projects = [
  {
    id: "benitosilvestre",
    name: "Benito Silvestre Abogados",
    domain: "benitosilvestreabogados.es",
    url: "https://benitosilvestreabogados.es/",
    image: "/projects/benitosilvestre.jpg",
    category: "Servicios Legales",
    year: "2026",
    description:
      "Web institucional para un despacho de abogados especializado en extranjería, nacionalidad y derecho penal. Diseño centrado en confianza, captación de consultas y posicionamiento local en Burgos.",
    tags: ["Diseño Web", "SEO Local", "Branding"],
    span: "md:col-span-7",
    featured: true,
  },
  {
    id: "resolvercrimenes",
    name: "Resolver Crímenes",
    domain: "resolvercrimenes.com",
    url: "https://resolvercrimenes.com/",
    image: "/projects/resolvercrimenes.jpg",
    category: "SaaS / IA",
    year: "2026",
    description:
      "Plataforma interactiva que combina IA y casos criminales reales. Sistema de suscripción, casos jugables e interrogatorios con personajes IA.",
    tags: ["E-commerce", "Membresía", "UX"],
    span: "md:col-span-5",
  },
  {
    id: "rubicars",
    name: "Rubicars",
    domain: "rubicars.com",
    url: "https://rubicars.com/",
    image: "/projects/rubicars.jpg",
    category: "Automoción",
    year: "2026",
    description:
      "Catálogo profesional de venta de vehículos de segunda mano con fichas técnicas, testimonios y captación vía WhatsApp.",
    tags: ["Catálogo", "Diseño Web", "Lead Gen"],
    span: "md:col-span-5",
  },
  {
    id: "plaselec",
    name: "Mecanizados Plaselec",
    domain: "plaselec.com",
    url: "https://plaselec.com/",
    image: "/projects/plaselec.jpg",
    category: "Industrial / B2B",
    year: "2025",
    description:
      "Web corporativa B2B para empresa de mecanizado y granallado de precisión certificada ISO 9001:2015. Foco en confianza y captación industrial.",
    tags: ["Diseño Web", "B2B", "SEO"],
    span: "md:col-span-7",
  },
  {
    id: "cokocbd",
    name: "Coko CBD",
    domain: "cokocbd.com",
    url: "https://cokocbd.com/",
    image: "/projects/cokocbd.jpg",
    category: "E-commerce",
    year: "2025",
    description:
      "Tienda online completa de productos CBD con catálogo extenso, variantes de producto, blog SEO y suscripción a newsletter.",
    tags: ["WooCommerce", "SEO", "E-commerce"],
    span: "md:col-span-6",
  },
  {
    id: "ideasdenegocio",
    name: "Ideas de Negocio Club",
    domain: "ideasdenegocio.club",
    url: "https://ideasdenegocio.club/",
    image: "/projects/ideasdenegocio.jpg",
    category: "Contenido / Membresía",
    year: "2025",
    description:
      "Club de contenido para emprendedores con sistema de membresía premium, newsletter, categorías por niveles y traducción multi-idioma.",
    tags: ["Membresía", "Contenido", "Multi-idioma"],
    span: "md:col-span-6",
  },
];

const fallback = (url) =>
  `https://s.wordpress.com/mshots/v1/${encodeURIComponent(url)}?w=1280&h=820`;

const cardVariant = {
  hidden: { opacity: 0, y: 32 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Portfolio() {
  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="relative py-24 md:py-32 border-t border-white/15"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Section header */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 lg:mb-20 items-end">
          <div className="md:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span
                className="h-px w-10"
                style={{ background: "var(--tvac-primary)" }}
              />
              <p className="font-display text-xs font-bold tracking-[0.25em] uppercase text-white/75">
                / 01 — Portfolio
              </p>
            </div>
            <h2
              data-testid="portfolio-heading"
              className="font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter uppercase leading-[0.9]"
            >
              Proyectos que ya <br className="hidden md:block" />
              <span className="text-volt">nos conocen.</span>
            </h2>
          </div>
          <p className="md:col-span-5 text-base md:text-lg text-zinc-400 leading-relaxed">
            Una selección de marcas, despachos, e-commerce y plataformas a las
            que ayudamos a destacar online. Diseño, SEO, contenido y conversión
            — todo medido, todo escalable.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6">
          {projects.map((p, i) => (
            <motion.a
              key={p.id}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={cardVariant}
              data-testid={`project-card-${p.id}`}
              className={`project-card group block ${p.span}`}
            >
              {/* Thumbnail */}
              <div
                className={`relative w-full overflow-hidden ${
                  p.featured ? "aspect-[16/10]" : "aspect-[16/11]"
                }`}
              >
                <img
                  src={p.image}
                  alt={`Captura de ${p.name}`}
                  loading="lazy"
                  className="project-thumb absolute inset-0 w-full h-full object-cover object-top"
                  onError={(e) => {
                    if (e.currentTarget.dataset.fb !== "1") {
                      e.currentTarget.dataset.fb = "1";
                      e.currentTarget.src = fallback(p.url);
                    }
                  }}
                />
                {/* Overlay */}
                <div className="project-overlay absolute inset-0 pointer-events-none" />

                {/* Top-left meta */}
                <div className="absolute top-5 left-5 right-5 z-10 flex items-start justify-between gap-3">
                  <span className="tag-pill">
                    <span
                      className="h-1.5 w-1.5 rounded-full"
                      style={{ background: "var(--tvac-primary)" }}
                    />
                    {p.category}
                  </span>
                  <span className="tag-pill">{p.year}</span>
                </div>

                {/* Bottom-left domain */}
                <div className="absolute bottom-5 left-5 right-5 z-10 flex items-end justify-between gap-3">
                  <p className="font-display text-[11px] font-bold tracking-[0.2em] uppercase text-white">
                    {p.domain}
                  </p>
                  <span
                    className="inline-flex items-center justify-center h-10 w-10 border border-white/30 bg-[#122a7a]/50 backdrop-blur-md text-white transition-all group-hover:bg-[var(--tvac-primary)] group-hover:text-[#122a7a] group-hover:border-[var(--tvac-primary)] group-hover:-rotate-45"
                    aria-hidden
                  >
                    <ArrowUpRight size={16} weight="bold" />
                  </span>
                </div>
              </div>

              {/* Body */}
              <div className="p-6 md:p-7 flex flex-col gap-4 border-t border-white/15">
                <div className="flex items-start justify-between gap-4">
                  <h3
                    className={`font-display font-bold tracking-tight ${
                      p.featured
                        ? "text-2xl md:text-3xl"
                        : "text-xl md:text-2xl"
                    }`}
                  >
                    {p.name}
                  </h3>
                </div>
                <p className="text-sm text-white/80 leading-relaxed">
                  {p.description}
                </p>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  {p.tags.map((t) => (
                    <span key={t} className="tag-pill text-[10px]">
                      {t}
                    </span>
                  ))}
                </div>
                <div className="pt-2">
                  <span className="visit-link" data-testid={`project-visit-${p.id}`}>
                    Visitar Web
                    <ArrowUpRight size={14} weight="bold" />
                  </span>
                </div>
              </div>
            </motion.a>
          ))}
        </div>

        {/* Bottom CTA strip */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="mt-16 lg:mt-20 border border-white/20 p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6 bg-[#15318f]"
        >
          <div>
            <p className="font-display text-xs font-bold tracking-[0.25em] uppercase text-white/70 mb-3">
              / Tu proyecto · El siguiente
            </p>
            <h3 className="font-display text-2xl md:text-3xl lg:text-4xl font-black tracking-tight uppercase">
              ¿Quieres aparecer aquí?
            </h3>
          </div>
          <a
            href="https://wa.me/34686881546?text=Hola%2C%20quiero%20m%C3%A1s%20informaci%C3%B3n"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="portfolio-bottom-cta"
            className="btn-volt text-xs px-6 py-4 self-start md:self-auto"
          >
            Cuéntanos tu Proyecto
            <ArrowUpRight size={16} weight="bold" />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
